import React, { useState } from 'react';
import { createGroup } from '../features/expenseSplitter/groupService';
import { navigateToGroup } from '../services/router';
import CurrencyPicker from '../components/CurrencyPicker';
import { DEFAULT_CURRENCY } from '../utils/currency';

/**
 * Landing Page
 * 
 * Entry point for the expense splitter application.
 * Handles:
 * - New group creation
 * - Recent groups access (if any in localStorage)
 * - Demo/sample group loading
 * - Basic app introduction
 * 
 * User Journey: "Land on lightweight page → New group"
 */
const Landing: React.FC = () => {
  const [isCreating, setIsCreating] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [creatorName, setCreatorName] = useState('You');

  const handleCreateGroup = () => {
    setIsCreating(true);
    
    try {
      // Create a new group with user settings
      const result = createGroup({
        name: groupName.trim() || `Group ${new Date().toLocaleDateString()}`,
        creatorName: creatorName.trim() || 'You',
        defaultCurrency: currency
      });

      // Navigate to the group page
      navigateToGroup(result.group.id);
      
    } catch (error) {
      console.error('Failed to create group:', error);
      alert('Failed to create group. Please try again.');
      setIsCreating(false);
    }
  };

  return (
    <div className="landing-page content" style={{ maxWidth: '600px', margin: '0 auto', marginTop: '60px' }}>
      <h1>Expense Splitter</h1>
      <p>Get square in seconds. Split expenses fairly with your group - no signup required, works offline.</p>
      
      <div style={{ marginTop: '32px' }}>
        {/* Quick Create Button */}
        {!showAdvanced && (
          <div style={{ textAlign: 'center' }}>
            <button 
              className="btn btn-primary" 
              onClick={handleCreateGroup}
              disabled={isCreating}
              style={{ marginBottom: '16px' }}
            >
              {isCreating ? 'Creating...' : 'Create New Group'}
            </button>
            <div>
              <button 
                onClick={() => setShowAdvanced(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--color-gray-600)',
                  fontSize: '14px',
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
              >
                Customize group settings
              </button>
            </div>
          </div>
        )}

        {/* Advanced Settings */}
        {showAdvanced && (
          <div style={{ marginTop: '24px' }}>
            <h3 style={{ marginBottom: '20px' }}>Group Settings</h3>
            
            {/* Group Name */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontSize: '14px', 
                fontWeight: 'var(--font-weight-medium)' 
              }}>
                Group Name
              </label>
              <input
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="e.g., Weekend Trip, Dinner Party"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--color-gray-200)',
                  borderRadius: 'var(--radius-base)',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Your Name */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontSize: '14px', 
                fontWeight: 'var(--font-weight-medium)' 
              }}>
                Your Name
              </label>
              <input
                type="text"
                value={creatorName}
                onChange={(e) => setCreatorName(e.target.value)}
                placeholder="Your name"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--color-gray-200)',
                  borderRadius: 'var(--radius-base)',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Currency */}
            <div style={{ marginBottom: '24px' }}>
              <CurrencyPicker
                value={currency}
                onChange={setCurrency}
                label="Default Currency"
                size="medium"
              />
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button 
                className="btn btn-primary" 
                onClick={handleCreateGroup}
                disabled={isCreating}
                style={{ flex: 1 }}
              >
                {isCreating ? 'Creating...' : 'Create Group'}
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowAdvanced(false)}
                disabled={isCreating}
              >
                Back
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: '24px', fontSize: '14px', color: 'var(--color-gray-600)' }}>
        <p>✓ No account required</p>
        <p>✓ Works offline</p>
        <p>✓ Share via link</p>
        <p>✓ Privacy-first</p>
      </div>
    </div>
  );
};

export default Landing;
